import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  ArrowRight,
  ArrowLeft,
  Check,
  AlertCircle,
  Info,
  Briefcase,
  MapPin,
  Phone,
  Mail,
  Globe,
  User,
  FileText,
  CheckCircle,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface FormData {
  name: string;
  companyType: string;
  industry: string;
  businessNumber: string;
  ceoName: string;
  address: string;
  phone: string;
  email: string;
  website: string;
  description: string;
}

const companyTypes = [
  { value: 'LARGE', label: '대기업' },
  { value: 'MIDSIZE', label: '중견기업' },
  { value: 'SMALL', label: '중소기업' },
  { value: 'STARTUP', label: '스타트업' },
  { value: 'NONPROFIT', label: '비영리기관' },
  { value: 'RESEARCH', label: '연구기관' },
  { value: 'ASSOCIATION', label: '협회' },
  { value: 'UNIVERSITY', label: '대학' },
  { value: 'PUBLIC', label: '공공기관' },
];

const industries = [
  { value: 'IT', label: 'IT/소프트웨어' },
  { value: 'MANUFACTURING', label: '제조업' },
  { value: 'BIO', label: '바이오/헬스케어' },
  { value: 'ENERGY', label: '에너지/환경' },
  { value: 'CONTENTS', label: '콘텐츠/미디어' },
  { value: 'FINANCE', label: '금융/핀테크' },
  { value: 'AGRICULTURE', label: '농업/식품' },
  { value: 'LOGISTICS', label: '물류/유통' },
  { value: 'CONSTRUCTION', label: '건설/부동산' },
  { value: 'OTHER', label: '기타' },
];

const getApiBaseUrl = (): string => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8001';
  }
  return `${protocol}//${hostname}:8001`;
};

const CompanyRegistration: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    name: '',
    companyType: '',
    industry: '',
    businessNumber: '',
    ceoName: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    description: '',
  });

  const updateField = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const calculateCompleteness = (): number => {
    const fields = [
      formData.name,
      formData.companyType,
      formData.industry,
      formData.businessNumber,
      formData.ceoName,
      formData.address,
      formData.phone,
      formData.email,
      formData.description,
    ];
    const filled = fields.filter(f => f && f.trim() !== '').length;
    return Math.round((filled / fields.length) * 100);
  };

  const validateStep1 = (): boolean => {
    if (!formData.name.trim()) {
      setError('기업명을 입력해주세요');
      return false;
    }
    if (!formData.companyType) {
      setError('기업 유형을 선택해주세요');
      return false;
    }
    if (!formData.industry) {
      setError('산업 분야를 선택해주세요');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (currentStep === 1 && !validateStep1()) {
      return;
    }
    setCurrentStep(prev => Math.min(prev + 1, 3));
    setError(null);
  };

  const handlePrev = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    setError(null);
  };

  const handleSubmit = async () => {
    if (!isAuthenticated) {
      setError('로그인이 필요합니다');
      navigate('/login');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${getApiBaseUrl()}/api/companies/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: formData.name,
          company_type: formData.companyType,
          industry: formData.industry,
          business_number: formData.businessNumber || null,
          ceo_name: formData.ceoName || null,
          address: formData.address || null,
          phone: formData.phone || null,
          email: formData.email || null,
          website: formData.website || null,
          description: formData.description || null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || '기업 등록에 실패했습니다');
      }

      setIsSuccess(true);
    } catch (err: any) {
      setError(err.message || '기업 등록 중 오류가 발생했습니다');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="card text-center py-12">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">기업 등록 완료!</h1>
          <p className="text-gray-600 mb-6">
            기업 등록이 성공적으로 완료되었습니다.<br />
            관리자 승인 후 모든 기능을 이용하실 수 있습니다.
          </p>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
            <div className="flex items-start">
              <Info className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">다음 단계</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>관리자 승인을 기다려주세요 (평균 1-2일 소요)</li>
                  <li>승인 완료 시 이메일로 안내드립니다</li>
                  <li>승인 후 포트폴리오를 등록하여 프로필을 완성하세요</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex justify-center space-x-4">
            <button
              onClick={() => navigate('/company-pool')}
              className="btn-primary"
            >
              기업 데이터풀로 이동
            </button>
            <button
              onClick={() => navigate('/')}
              className="btn-secondary"
            >
              대시보드로 이동
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <Building2 className="h-7 w-7 mr-3 text-emerald-600" />
          기업/기관 등록
        </h1>
        <p className="text-gray-600 mt-2">
          기업 정보를 등록하여 공모사업 매칭 및 컨소시엄 구성에 참여하세요.
        </p>
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {[1, 2, 3].map((step) => (
            <div key={step} className="flex items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${
                  currentStep >= step
                    ? 'bg-emerald-600 text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {currentStep > step ? <Check className="h-5 w-5" /> : step}
              </div>
              <div className="ml-3 hidden sm:block">
                <p className={`font-medium ${currentStep >= step ? 'text-gray-900' : 'text-gray-500'}`}>
                  {step === 1 && '기본 정보'}
                  {step === 2 && '상세 정보'}
                  {step === 3 && '확인 및 제출'}
                </p>
              </div>
              {step < 3 && (
                <div
                  className={`w-16 sm:w-24 h-1 mx-4 rounded ${
                    currentStep > step ? 'bg-emerald-600' : 'bg-gray-200'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
          <AlertCircle className="h-5 w-5 text-red-600 mr-3 flex-shrink-0" />
          <span className="text-red-800">{error}</span>
        </div>
      )}

      {/* Form Content */}
      <div className="card">
        {/* Step 1: 기본 정보 */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">기본 정보</h2>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                기업명 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => updateField('name', e.target.value)}
                placeholder="예: (주)테크솔루션"
                className="input-field"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  기업 유형 <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.companyType}
                  onChange={(e) => updateField('companyType', e.target.value)}
                  className="input-field"
                >
                  <option value="">선택하세요</option>
                  {companyTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  산업 분야 <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.industry}
                  onChange={(e) => updateField('industry', e.target.value)}
                  className="input-field"
                >
                  <option value="">선택하세요</option>
                  {industries.map((ind) => (
                    <option key={ind.value} value={ind.value}>
                      {ind.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                사업자등록번호
              </label>
              <input
                type="text"
                value={formData.businessNumber}
                onChange={(e) => updateField('businessNumber', e.target.value)}
                placeholder="예: 123-45-67890"
                className="input-field"
              />
              <p className="text-sm text-gray-500 mt-1">
                사업자등록번호를 입력하면 기업 인증에 활용됩니다.
              </p>
            </div>
          </div>
        )}

        {/* Step 2: 상세 정보 */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">상세 정보</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <User className="h-4 w-4 inline mr-1" />
                  대표자명
                </label>
                <input
                  type="text"
                  value={formData.ceoName}
                  onChange={(e) => updateField('ceoName', e.target.value)}
                  placeholder="홍길동"
                  className="input-field"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Phone className="h-4 w-4 inline mr-1" />
                  연락처
                </label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                  placeholder="02-1234-5678"
                  className="input-field"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Mail className="h-4 w-4 inline mr-1" />
                이메일
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => updateField('email', e.target.value)}
                placeholder="contact@company.com"
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Globe className="h-4 w-4 inline mr-1" />
                웹사이트
              </label>
              <input
                type="url"
                value={formData.website}
                onChange={(e) => updateField('website', e.target.value)}
                placeholder="https://company.com"
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <MapPin className="h-4 w-4 inline mr-1" />
                주소
              </label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => updateField('address', e.target.value)}
                placeholder="서울시 강남구 테헤란로 123"
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FileText className="h-4 w-4 inline mr-1" />
                기업 소개
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => updateField('description', e.target.value)}
                placeholder="기업에 대한 간단한 소개를 작성해주세요."
                rows={4}
                className="input-field"
              />
            </div>
          </div>
        )}

        {/* Step 3: 확인 및 제출 */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">입력 정보 확인</h2>

            {/* 프로필 완성도 */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">프로필 완성도</span>
                <span className="text-sm font-bold text-emerald-600">{calculateCompleteness()}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-emerald-600 h-2 rounded-full transition-all"
                  style={{ width: `${calculateCompleteness()}%` }}
                />
              </div>
              {calculateCompleteness() < 80 && (
                <p className="text-sm text-amber-600 mt-2">
                  💡 더 많은 정보를 입력하면 매칭 정확도가 높아집니다.
                </p>
              )}
            </div>

            {/* 입력 정보 요약 */}
            <div className="border border-gray-200 rounded-lg divide-y">
              <div className="p-4">
                <h3 className="font-medium text-gray-900 mb-3">기본 정보</h3>
                <dl className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <dt className="text-gray-500">기업명</dt>
                    <dd className="font-medium text-gray-900">{formData.name || '-'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">기업 유형</dt>
                    <dd className="font-medium text-gray-900">
                      {companyTypes.find(t => t.value === formData.companyType)?.label || '-'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">산업 분야</dt>
                    <dd className="font-medium text-gray-900">
                      {industries.find(i => i.value === formData.industry)?.label || '-'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">사업자등록번호</dt>
                    <dd className="font-medium text-gray-900">{formData.businessNumber || '-'}</dd>
                  </div>
                </dl>
              </div>

              <div className="p-4">
                <h3 className="font-medium text-gray-900 mb-3">상세 정보</h3>
                <dl className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <dt className="text-gray-500">대표자명</dt>
                    <dd className="font-medium text-gray-900">{formData.ceoName || '-'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">연락처</dt>
                    <dd className="font-medium text-gray-900">{formData.phone || '-'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">이메일</dt>
                    <dd className="font-medium text-gray-900">{formData.email || '-'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">웹사이트</dt>
                    <dd className="font-medium text-gray-900">{formData.website || '-'}</dd>
                  </div>
                  <div className="col-span-2">
                    <dt className="text-gray-500">주소</dt>
                    <dd className="font-medium text-gray-900">{formData.address || '-'}</dd>
                  </div>
                  <div className="col-span-2">
                    <dt className="text-gray-500">기업 소개</dt>
                    <dd className="font-medium text-gray-900">{formData.description || '-'}</dd>
                  </div>
                </dl>
              </div>
            </div>

            {/* 안내 사항 */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <Info className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">등록 안내</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>등록된 기업은 관리자 승인 후 활성화됩니다.</li>
                    <li>승인 후 포트폴리오를 등록하여 공모사업 매칭에 참여할 수 있습니다.</li>
                    <li>입력된 정보는 언제든 수정할 수 있습니다.</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8 pt-6 border-t">
          <button
            onClick={handlePrev}
            disabled={currentStep === 1}
            className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
              currentStep === 1
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            이전
          </button>

          {currentStep < 3 ? (
            <button
              onClick={handleNext}
              className="btn-primary flex items-center"
            >
              다음
              <ArrowRight className="h-5 w-5 ml-2" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="btn-primary flex items-center"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                  등록 중...
                </>
              ) : (
                <>
                  <Check className="h-5 w-5 mr-2" />
                  기업 등록
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyRegistration;
